import React, { useState } from 'react';
import { useApp } from '../context/AppContext';
import {
  Send, Pin, Mail, Bell, X, Check, Users, User, Calendar
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import Modal from './Modal';
import { postAnnouncement, postDirect } from '../utils/slackApi';
import './AnnouncementComposer.css';

function AnnouncementComposer({ isOpen, onClose, onSuccess }) {
  const { state, createAnnouncement, createDirectMessage } = useApp();
  const { employees } = state;

  const [form, setForm] = useState({
    sendTo: 'all',
    employeeId: '',
    subject: '',
    body: '',
    isPinned: true,
    sendEmail: true,
    requiresAcknowledgment: false,
    expiresAt: ''
  });

  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const activeEmployees = employees.filter(e => e.status === 'active');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.subject.trim() || !form.body.trim()) return;

    setSending(true);

    try {
      if (form.sendTo === 'all') {
        await createAnnouncement({
          subject: form.subject,
          body: form.body,
          isPinned: form.isPinned,
          sendEmail: form.sendEmail,
          requiresAcknowledgment: form.requiresAcknowledgment,
          expiresAt: form.expiresAt || null
        });

        // Fan out to Slack #all-staff (best-effort; Firestore write is authoritative)
        postAnnouncement({
          subject: form.subject,
          body: form.body,
          fromName: 'Management'
        }).catch(err => console.error('Slack announcement fan-out failed:', err));

        // If sendEmail is true, send email to all employees
        if (form.sendEmail) {
          const employeesWithEmail = activeEmployees.filter(e => e.email);
          if (employeesWithEmail.length > 0) {
            try {
              await fetch('/.netlify/functions/send-email', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  type: 'internal_announcement',
                  data: {
                    recipients: employeesWithEmail.map(e => ({
                      email: e.email,
                      name: `${e.firstName} ${e.lastName || ''}`.trim()
                    })),
                    subject: form.subject,
                    body: form.body
                  }
                })
              });
            } catch (emailError) {
              console.error('Failed to send announcement email:', emailError);
            }
          }
        }
      } else {
        // Direct message to individual employee
        await createDirectMessage(form.employeeId, {
          subject: form.subject,
          body: form.body,
          fromName: 'Management'
        });

        // Fan out to Slack DM if we have a slackUserId for this employee
        const recipient = employees.find(e => e.id === form.employeeId);
        if (recipient?.slackUserId) {
          postDirect({
            slackUserId: recipient.slackUserId,
            subject: form.subject,
            body: form.body
          }).catch(err => console.error('Slack DM fan-out failed:', err));
        }
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        setForm({
          sendTo: 'all',
          employeeId: '',
          subject: '',
          body: '',
          isPinned: true,
          sendEmail: true,
          requiresAcknowledgment: false,
          expiresAt: ''
        });
        onSuccess?.();
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error creating announcement:', error);
      alert('Failed to send announcement. Please try again.');
    } finally {
      setSending(false);
    }
  };

  if (success) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Send Announcement">
        <div className="success-state">
          <div className="success-icon">
            <Check size={32} />
          </div>
          <h3>Announcement Sent!</h3>
          <p>
            {form.sendTo === 'all' 
              ? `Sent to ${activeEmployees.length} employees`
              : 'Message sent successfully'
            }
          </p>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Announcement">
      <form onSubmit={handleSubmit} className="announcement-form">
        {/* Send To */}
        <div className="form-group">
          <label>Send To</label>
          <div className="send-to-options">
            <button
              type="button"
              className={`send-to-btn ${form.sendTo === 'all' ? 'active' : ''}`}
              onClick={() => setForm({ ...form, sendTo: 'all', employeeId: '' })}
            >
              <Users size={18} />
              All Employees
            </button>
            <button
              type="button"
              className={`send-to-btn ${form.sendTo === 'individual' ? 'active' : ''}`}
              onClick={() => setForm({ ...form, sendTo: 'individual' })}
            >
              <User size={18} />
              Individual
            </button>
          </div>
        </div>

        {/* Individual Employee Select */}
        {form.sendTo === 'individual' && (
          <div className="form-group">
            <label>Select Employee</label>
            <select
              value={form.employeeId}
              onChange={(e) => setForm({ ...form, employeeId: e.target.value })}
              required
            >
              <option value="">Choose employee...</option>
              {activeEmployees.map(emp => (
                <option key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName} ({emp.role})
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Subject */}
        <div className="form-group">
          <label>Subject</label>
          <input
            type="text"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            placeholder="Enter subject line..."
            required
          />
        </div>

        {/* Message Body */}
        <div className="form-group">
          <label>Message</label>
          <textarea
            value={form.body}
            onChange={(e) => setForm({ ...form, body: e.target.value })}
            placeholder="Type your message here..."
            rows={6}
            required
          />
        </div>

        {/* Options (only for announcements to all) */}
        {form.sendTo === 'all' && (
          <div className="form-group">
            <label>Options</label>
            <div className="checkbox-options">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={form.isPinned}
                  onChange={(e) => setForm({ ...form, isPinned: e.target.checked })}
                />
                <div className="option-content">
                  <Pin size={16} />
                  <div>
                    <strong>Pin this message</strong>
                    <span>Keep at top of employee inbox</span>
                  </div>
                </div>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={form.sendEmail}
                  onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })}
                />
                <div className="option-content">
                  <Mail size={16} />
                  <div>
                    <strong>Send email notification</strong>
                    <span>Also send to employee email addresses</span>
                  </div>
                </div>
              </label>

              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={form.requiresAcknowledgment}
                  onChange={(e) => setForm({ ...form, requiresAcknowledgment: e.target.checked })}
                />
                <div className="option-content">
                  <Check size={16} />
                  <div>
                    <strong>Require acknowledgment</strong>
                    <span>Employees must confirm they've read this</span>
                  </div>
                </div>
              </label>
            </div>
          </div>
        )}

        {/* Pin Expiration */}
        {form.sendTo === 'all' && form.isPinned && (
          <div className="form-group">
            <label>Pin Expiration (optional)</label>
            <input
              type="date"
              value={form.expiresAt}
              onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
              min={format(addDays(new Date(), 1), 'yyyy-MM-dd')}
            />
            <span className="help-text">Leave blank to keep pinned indefinitely</span>
          </div>
        )}

        {/* Actions */}
        <div className="form-actions">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={sending || !form.subject.trim() || !form.body.trim()}
          >
            {sending ? (
              'Sending...'
            ) : (
              <>
                <Send size={16} />
                {form.sendTo === 'all' ? 'Send Announcement' : 'Send Message'}
              </>
            )}
          </button>
        </div>
      </form>
    </Modal>
  );
}

export default AnnouncementComposer;
